'use client';

import React, { useState, useEffect } from 'react';
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow
} from '@nextui-org/react';
import { Trash } from 'lucide-react';
import { API_URL } from '../../utils/constants';
import auth from '../../Auth';
import Breadcrumbs from '../Breadcrumbs';

function ApiTokensPage() {
  const [tokens, setTokens] = useState([]);
  const [showModal, setShowModal] = useState(false); // To control modal visibility
  const [newToken, setNewToken] = useState('');
  const [activePopover, setActivePopover] = useState(null);

  const fetchTokens = () => {
    fetch(`${API_URL}/api_tokens`, {
      headers: {
        Authorization: 'Bearer ' + auth.syncGetIdToken(),
        'Content-Type': 'application/json'
      }
    })
      .then((response) => response.json())
      .then((data) => setTokens(data))
      .catch((error) => console.error('Error fetching tokens:', error));
  };

  // Fetch API tokens
  useEffect(() => {
    fetchTokens();
  }, []);

  // Function to request a new token
  const requestNewToken = () => {
    fetch(`${API_URL}/api_tokens`, {
      method: 'POST',
      headers: {
        Authorization: 'Bearer ' + auth.syncGetIdToken(),
        'Content-Type': 'application/json'
      }
    })
      .then((response) => response.json())
      .then((data) => {
        setNewToken(data.api_token);
        setShowModal(true);
      })
      .catch((error) => console.error('Error requesting new token:', error));
  };

  // Function to copy token to clipboard
  const copyTokenToClipboard = () => {
    navigator.clipboard
      .writeText(newToken)
      .then(() => {
        console.log('Token copied to clipboard');
      })
      .catch((error) => {
        console.error('Error copying token to clipboard:', error);
      });
  };

  // Function to delete a token
  const deleteToken = (masked_token) => {
    console.log('Deleting token:', masked_token);
    fetch(`${API_URL}/api_tokens`, {
      method: 'DELETE',
      body: JSON.stringify({ masked_token: masked_token }),
      headers: {
        Authorization: 'Bearer ' + auth.syncGetIdToken(),
        'Content-Type': 'application/json'
      }
    })
      .then(() => {
        fetchTokens();
      })
      .catch((error) => console.error('Error deleting token:', error));
  };
  function tmsToDateTime(tms) {
    tms = parseInt(tms);
    return new Date(tms).toUTCString();
  }

  const closeModal = () => {
    setShowModal(false);
    fetchTokens();
  };
  const TokenPopup = ({ isOpen, onClose, token, onCopy }) => {
    if (!isOpen) return null;
    return (
      <div className='fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full ' id='my-modal'>
        <div className='relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white'>
          <div className='mt-3 text-center'>
            <div className='mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100'>{/* Icon or Image */}</div>
            <h3 className='text-lg leading-6 font-medium text-gray-900'>Your New API Token</h3>
            <div className='mt-2 px-7 py-3'>
              <p className='text-sm text-gray-500'>
                This is your new API token. Please copy it now. For security reasons, it won't be displayed again.
              </p>
              <input
                readOnly
                value={token}
                className='mt-3 text-center w-full px-3 py-2 text-gray-700 border rounded-lg focus:outline-none'
              />
            </div>
            <div className='items-center px-4 py-3'>
              <button
                id='ok-btn'
                onClick={onCopy}
                className='px-4 py-2 bg-green-500 text-white text-base font-medium rounded-md w-3/4 shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-300'
              >
                Copy
              </button>
            </div>
            <div className='items-center px-4 py-3'>
              <button
                onClick={onClose}
                className='px-4 py-2 bg-gray-500 text-white text-base font-medium rounded-md w-3/4 shadow-sm hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-300'
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const crumbs = [
    { path: '/', breadcrumb: 'Home' },
    { path: '/settings', breadcrumb: 'Settings' },
    { path: window.location.href, breadcrumb: 'API Tokens' }
  ];

  return (
    <div className='bg-white p-8'>
      <Breadcrumbs crumbs={crumbs} />
      <Button className='mt-8' color='primary' onClick={requestNewToken}>
        Request New Token
      </Button>
      <Table isHeaderSticky className='mt-4' aria-label='API Tokens Table'>
        <TableHeader>
          <TableColumn style={{ width: '40%' }}>TOKEN ID</TableColumn>
          <TableColumn style={{ width: '40%' }}>CREATED AT</TableColumn>
          <TableColumn style={{ width: '20%' }}>ACTIONS</TableColumn>
        </TableHeader>
        <TableBody>
          {tokens.map((token) => (
            <TableRow key={token.masked_token}>
              <TableCell>{token.masked_token}</TableCell>
              <TableCell>{tmsToDateTime(token.created_tms)}</TableCell>
              <TableCell>
                <Popover
                  isOpen={activePopover === token.masked_token}
                  onOpen={() => setActivePopover(token.masked_token)}
                  onClose={() => setActivePopover(null)}
                >
                  <PopoverTrigger>
                    <Button
                      color='danger'
                      aria-label='delete token'
                      onClick={() => setActivePopover(activePopover === token.masked_token ? null : token.masked_token)}
                    >
                      <Trash className='h-4 w-4' />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent>
                    <div className='p-4'>
                      <p className='font-bold mb-2'>Are you sure?</p>
                      <p className='text-sm mb-4'>This action cannot be undone. This will permanently delete this API Token</p>
                      <div className='flex justify-end gap-2'>
                        <Button color='default' aria-label='cancel delete token action' onClick={() => setActivePopover(null)}>
                          Cancel
                        </Button>
                        <Button
                          color='danger'
                          aria-label='confirm delete token action'
                          onClick={() => {
                            deleteToken(token.masked_token);
                            setActivePopover(null);
                          }}
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <TokenPopup isOpen={showModal} onClose={() => closeModal()} token={newToken} onCopy={copyTokenToClipboard} />
    </div>
  );
}

export default ApiTokensPage;
